import React, { useRef } from 'react';

import PropTypes from 'prop-types';

import amplitude from 'amplitude-js';
import { DiskButton } from 'apps/auth/components/common/DiskButton/DiskButton';
import FeeRatePopup from 'apps/auth/components/common/FeePanel/FeeRatePopup';
import { Row } from 'apps/auth/components/common/Layout/Row';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import { Text } from 'apps/auth/components/common/Text/Text';
import { transactionMethods } from 'apps/auth/components/utils/AmplitudeUtils';
import { GET_BETTER_RATE_CLICKED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { PAYMENT_CURRENCY, PAYOUT_COUNTRY, PAYOUT_CURRENCY, PAYOUT_METHOD } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { useExtra, usePageMessages } from 'apps/auth/hooks/selectors';

import { ListItemTitle } from './styles';

export const shouldShowImprovedBetterRate = (offers = [], currentValue) => {
    return offers.some((offer) => offer?.current && currentValue >= offer.from && currentValue <= offer.till);
};

const ExchangeRateInfoPureComponent = (props) => {
    const popupRef = useRef(null);
    const { sendFlowV3, commission, exchangeRatio, toCountry } = useExtra();
    const { betterRateInfo, improvedExchangeRateText, exchangeRateText, specialBonusRateInfo, newExchangeRateText } = usePageMessages();
    const isBetterRate = commission?.specialRateOffer?.offers?.length > 0;
    const isSpecialBonusRate = specialBonusRateInfo && commission?.bonusSpecialRate;
    const showExchangePopup = isSpecialBonusRate || isBetterRate;

    const getFeeColorText = (commissionInfo) => {
        return commissionInfo?.bonusSpecialRate ? 'emphasized' : 'darkGray';
    };

    const openExchangePopup = (e) => {
        e.preventDefault();
        if (isBetterRate) {
            const amplitudeDeliveryMethod = transactionMethods[props.deliveryMethod];
            amplitude.getInstance().logEvent(GET_BETTER_RATE_CLICKED, {
                [PAYOUT_COUNTRY]: toCountry?.nameEn,
                [PAYOUT_METHOD]: amplitudeDeliveryMethod,
                [PAYMENT_CURRENCY]: props.fromCurrencyIso,
                [PAYOUT_CURRENCY]: props.currencyToIso,
            });
        }
        popupRef.current?.open(
            <FeeRatePopup
                text={specialBonusRateInfo}
                isComment={true}
                isShowTerms={false}
                popupRef={popupRef}
                isBetterRate={isBetterRate}
                isSpecialBonusRate={isSpecialBonusRate}
                fromCurrencyIso={props.fromCurrencyIso}
                currencyToIso={props.currencyToIso}
                amountFrom={props.amountFrom}
            />,
        );
    };

    const getBetterRateName = () => {
        let text = betterRateInfo;
        const offers = commission?.specialRateOffer?.offers ?? [];
        const currentValue = props.amountFrom?.value;
        if (shouldShowImprovedBetterRate(offers, currentValue)) {
            text = improvedExchangeRateText;
        }
        return text;
    };

    const getRateName = () => {
        if (commission?.bonusSpecialRate) {
            return improvedExchangeRateText;
        } else if (isBetterRate) {
            return getBetterRateName();
        } else if (sendFlowV3) {
            return newExchangeRateText;
        } else {
            return exchangeRateText;
        }
    };

    return (
        <>
            <ListItemTitle showInfo={showExchangePopup} onClick={showExchangePopup ? openExchangePopup : null}>
                <Text size="secondary" color={isBetterRate && !isSpecialBonusRate ? 'primary' : 'secondary'} variant="div" indent="4px">
                    {getRateName()}
                </Text>
                {showExchangePopup && (
                    <DiskButton
                        size="small"
                        iconName="info-rounded"
                        iconColor={isBetterRate && !isSpecialBonusRate ? 'primary' : 'secondary'}
                        type="button"
                        raised={false}
                    />
                )}
            </ListItemTitle>
            <Text size="secondary" color={getFeeColorText(props.commissionInfo)} variant="div">
                {exchangeRatio.fromAmount}
                <Text size="secondary" color={getFeeColorText(props.commissionInfo)} smallCaps>{` ${exchangeRatio.fromCurrency}`}</Text>
                {' = '}
                <Row flex justify="center" align="center" type="span">
                    <Text size="secondary" color={getFeeColorText(props.commissionInfo)} nowrap>
                        {exchangeRatio.toAmount}
                    </Text>
                    <Text size="secondary" color={getFeeColorText(props.commissionInfo)} smallCaps>
                        &nbsp;{`${exchangeRatio.toCurrency}`}
                    </Text>
                </Row>
            </Text>
            <PopupNew ref={popupRef} />
        </>
    );
};

ExchangeRateInfoPureComponent.propTypes = {
    commissionInfo: PropTypes.object,
};

export const ExchangeRateInfo = ExchangeRateInfoPureComponent;
